import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Callout } from '@/components/ui/Callout'
import { useReference } from '@/lib/reference/use-reference'
import { ReferenceError } from '@/lib/reference'
import { REFERENCE_ERROR_MESSAGE } from '@/lib/reference/reference-context'
import {
  aircraftFields,
  airportFields,
  overlappingScopes,
  parseDayReport,
  ScheduleError,
  SCHEDULE_ERROR_MESSAGE,
  useSchedule,
  type AircraftDraft,
  type AirportDraft,
  type ImportPlan,
  type MissingRows,
  type PreparedFile,
} from '@/lib/schedule'

/**
 * Load one or more period files.
 *
 * The period is the whole mechanism. A file states the period it covers in its
 * own title, and importing it **replaces that period outright**: every leg
 * inside it comes from the file, and a leg that used to be there and is no
 * longer in it is withdrawn. Days outside are not read and not touched.
 *
 * Which is why the period is shown as two editable dates rather than inferred
 * silently, and why nothing is written until the counts have been on screen:
 * BUSINESS-RULES 4.2 asks for a declared scope and a preview before any write,
 * because the difference between "this file adds flights" and "this file is
 * now the truth for these fourteen days" is the difference between a correct
 * board and a board that quietly keeps cancelled flights.
 *
 * The second half of the screen is the reference rows the file needs and the
 * tables do not have: a tail that joined the fleet, a station that opened. They
 * are created with the import — refusing a real flight because a table is
 * behind would be worse — and they are shown here, with their guessed values,
 * to be corrected now or later in Settings.
 */
export function ImportDialog({ onClose }: { onClose: () => void }) {
  const { prepare, plan, missing, commit } = useSchedule()
  const { aircraft, countries, timezones, create } = useReference()
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<PreparedFile[]>([])
  const [drafts, setDrafts] = useState<MissingRows>({ aircraft: [], airports: [] })
  const [reading, setReading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview: ImportPlan | null = useMemo(
    () => (files.length > 0 ? plan(files) : null),
    [files, plan],
  )
  const overlaps = useMemo(
    () =>
      new Set(
        overlappingScopes(files.map((file) => ({ fileName: file.parsed.fileName, scope: file.scope }))),
      ),
    [files],
  )
  const skipped = files.reduce((total, file) => total + file.parsed.skipped.length, 0)

  // Drafts follow the file list. Edits survive a scope change, which is the
  // only other thing that moves on this screen.
  useEffect(() => {
    setDrafts(files.length > 0 ? missing(files) : { aircraft: [], airports: [] })
    // missing() is derived from the reference tables, which do not change while
    // this dialog is open; re-seeding on every render would discard edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  const fleetValues = useMemo(
    () => ({
      groups: [...new Set(aircraft.map((row) => row.group))].sort(),
      categories: [...new Set(aircraft.map((row) => row.category))].sort(),
      types: [...new Set(aircraft.map((row) => row.type))].sort(),
    }),
    [aircraft],
  )

  async function onPick(list: FileList | null) {
    if (!list || list.length === 0) return
    setReading(true)
    setError(null)
    try {
      const parsed = await Promise.all(Array.from(list).map((file) => parseDayReport(file)))
      setFiles((current) => [
        ...current.filter((file) => !parsed.some((entry) => entry.fileName === file.parsed.fileName)),
        ...parsed.map(prepare),
      ])
    } catch (thrown) {
      const code = thrown instanceof ScheduleError ? thrown.code : 'unknown'
      const name = thrown instanceof ScheduleError ? thrown.fileName : undefined
      setError(`${name ? `${name} — ` : ''}${SCHEDULE_ERROR_MESSAGE[code]}`)
    } finally {
      setReading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function onImport() {
    setImporting(true)
    setError(null)
    try {
      // Reference rows first: a station's zone is what places its legs on the
      // clock, so it has to exist before the legs are resolved.
      for (const draft of drafts.aircraft) await create('aircraft', aircraftFields(draft))
      for (const draft of drafts.airports) await create('airport', airportFields(draft))
      await commit(files, drafts)
      onClose()
    } catch (thrown) {
      setError(
        thrown instanceof ReferenceError
          ? `${REFERENCE_ERROR_MESSAGE[thrown.code]}${thrown.field ? ` (${thrown.field})` : ''}`
          : SCHEDULE_ERROR_MESSAGE.unknown,
      )
    } finally {
      setImporting(false)
    }
  }

  // The two fields the aircraft table refuses outright. Everything else about
  // a new row may be filled in later, in Settings, at leisure.
  const badDraft = drafts.aircraft.some(
    (draft) => !draft.group.trim() || !/\d/.test(draft.seatConfig),
  )

  const updateAircraft = (index: number, patch: Partial<AircraftDraft>) =>
    setDrafts((current) => ({
      ...current,
      aircraft: current.aircraft.map((row, position) =>
        position === index ? { ...row, ...patch } : row,
      ),
    }))

  const updateAirport = (index: number, patch: Partial<AirportDraft>) =>
    setDrafts((current) => ({
      ...current,
      airports: current.airports.map((row, position) =>
        position === index ? { ...row, ...patch } : row,
      ),
    }))

  return (
    <Modal title="Import schedule" onClose={onClose} width="860px">
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        multiple
        onChange={(event) => onPick(event.target.files)}
        className="sr-only"
      />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button variant="secondary" loading={reading} onClick={() => inputRef.current?.click()}>
          Choose files
        </Button>
        {files.length > 0 && (
          <Button variant="tertiary" onClick={() => setFiles([])}>
            Clear
          </Button>
        )}
      </div>

      {error && (
        <Callout tone="error" className="mt-4">
          {error}
        </Callout>
      )}

      {files.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-[16px] border border-hairline">
          <table className="w-full">
            <thead>
              <tr className="border-b border-hairline bg-surface-soft">
                <Th>File</Th>
                <Th className="w-[76px] text-right">Legs</Th>
                <Th className="w-[150px]">From</Th>
                <Th className="w-[150px]">To</Th>
                <Th className="w-[44px]" />
              </tr>
            </thead>
            <tbody>
              {files.map((file, index) => (
                <tr key={file.parsed.fileName} className="border-b border-hairline-soft last:border-b-0">
                  <td className="px-3 py-2">
                    <p className="t-body-sm truncate text-ink">{file.parsed.fileName}</p>
                    <p className="t-caption mt-0.5 flex items-center gap-2 text-muted">
                      {file.parsed.scopeDeclared ? 'Period from file' : 'Period from rows'}
                      {overlaps.has(file.parsed.fileName) && <Badge>OVERLAP</Badge>}
                    </p>
                  </td>
                  <td className="t-number-sm px-3 py-2 text-right text-ink">{file.legs.length}</td>
                  {(['from', 'to'] as const).map((edge) => (
                    <td key={edge} className="px-3 py-2">
                      <input
                        type="date"
                        aria-label={`${edge === 'from' ? 'From' : 'To'} — ${file.parsed.fileName}`}
                        value={file.scope[edge]}
                        onChange={(event) =>
                          setFiles((current) =>
                            current.map((entry, position) =>
                              position === index
                                ? { ...entry, scope: { ...entry.scope, [edge]: event.target.value } }
                                : entry,
                            ),
                          )
                        }
                        className="t-number-sm h-9 w-full rounded-[8px] border border-hairline bg-canvas
                                   px-2 text-ink outline-none focus:border-primary"
                      />
                    </td>
                  ))}
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      aria-label={`Remove ${file.parsed.fileName}`}
                      onClick={() =>
                        setFiles((current) => current.filter((_, position) => position !== index))
                      }
                      className="rounded-[8px] p-1.5 text-body transition-colors hover:bg-surface-strong"
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-5">
          <Count label="New" value={preview.added} />
          <Count label="Changed" value={preview.changed} />
          <Count label="Withdrawn" value={preview.removed} tone="down" />
          <Count label="Unchanged" value={preview.unchanged} />
          <Count label="Outside periods" value={preview.untouched} />
          {preview.unplaceable > 0 && (
            <Count label="Not placeable" value={preview.unplaceable} tone="down" />
          )}
          {skipped > 0 && <Count label="Rows skipped" value={skipped} tone="down" />}
        </div>
      )}

      {drafts.aircraft.length > 0 && (
        <DraftSection title="New aircraft" count={drafts.aircraft.length}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-hairline bg-surface-soft">
                <Th className="w-[92px]">Regn</Th>
                <Th className="w-[60px] text-right">Legs</Th>
                <Th>Group</Th>
                <Th>Cat</Th>
                <Th>Type</Th>
                <Th className="w-[110px]">Seats</Th>
              </tr>
            </thead>
            <tbody>
              {drafts.aircraft.map((draft, index) => (
                <tr key={draft.regn} className="border-b border-hairline-soft last:border-b-0">
                  <td className="t-number-sm px-3 py-2 text-ink">{draft.regn}</td>
                  <td className="t-number-sm px-3 py-2 text-right text-muted">{draft.legs}</td>
                  <td className="px-2 py-2">
                    <Field
                      label={`Group ${draft.regn}`}
                      value={draft.group}
                      options={fleetValues.groups}
                      invalid={!draft.group.trim()}
                      onChange={(value) => updateAircraft(index, { group: value })}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Field
                      label={`Category ${draft.regn}`}
                      value={draft.category}
                      options={fleetValues.categories}
                      onChange={(value) => updateAircraft(index, { category: value })}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Field
                      label={`Type ${draft.regn}`}
                      value={draft.type}
                      options={fleetValues.types}
                      onChange={(value) => updateAircraft(index, { type: value })}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Field
                      label={`Seats ${draft.regn}`}
                      value={draft.seatConfig}
                      invalid={!/\d/.test(draft.seatConfig)}
                      onChange={(value) => updateAircraft(index, { seatConfig: value })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DraftSection>
      )}

      {drafts.airports.length > 0 && (
        <DraftSection title="New stations" count={drafts.airports.length}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-hairline bg-surface-soft">
                <Th className="w-[60px]">IATA</Th>
                <Th className="w-[60px] text-right">Legs</Th>
                <Th>Name</Th>
                <Th className="w-[92px]">Country</Th>
                <Th className="w-[190px]">Timezone</Th>
              </tr>
            </thead>
            <tbody>
              {drafts.airports.map((draft, index) => (
                <tr key={draft.iata} className="border-b border-hairline-soft last:border-b-0">
                  <td className="t-number-sm px-3 py-2 text-ink">{draft.iata}</td>
                  <td className="t-number-sm px-3 py-2 text-right text-muted">{draft.legs}</td>
                  <td className="px-2 py-2">
                    <Field
                      label={`Name ${draft.iata}`}
                      value={draft.name}
                      onChange={(value) => updateAirport(index, { name: value })}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Field
                      label={`Country ${draft.iata}`}
                      value={draft.countryCode}
                      options={countries.map((row) => row.code)}
                      onChange={(value) => updateAirport(index, { countryCode: value.toUpperCase() })}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Field
                      label={`Timezone ${draft.iata}`}
                      value={draft.timezone}
                      options={timezones.map((row) => row.name)}
                      invalid={!draft.timezone}
                      onChange={(value) => updateAirport(index, { timezone: value })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DraftSection>
      )}

      <div className="mt-8 flex gap-3">
        <Button
          fullWidth
          loading={importing}
          disabled={
            files.length === 0 ||
            badDraft ||
            files.some((file) => file.scope.from > file.scope.to)
          }
          onClick={onImport}
        >
          Import
        </Button>
        <Button variant="secondary" fullWidth onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  )
}

function DraftSection({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: ReactNode
}) {
  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="t-title-sm text-ink">{title}</h3>
        <Badge>{count}</Badge>
      </div>
      <div className="overflow-hidden rounded-[16px] border border-hairline">{children}</div>
    </div>
  )
}

/** A text field with suggestions, matching the filter fields on the board. */
function Field({
  label,
  value,
  options,
  invalid,
  onChange,
}: {
  label: string
  value: string
  options?: string[]
  invalid?: boolean
  onChange: (value: string) => void
}) {
  const id = `${label.replace(/\W+/g, '-')}-list`
  return (
    <>
      <input
        aria-label={label}
        value={value}
        list={options ? id : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={`t-body-sm h-9 w-full rounded-[8px] border bg-canvas px-2 text-ink outline-none
                    focus:border-primary ${invalid ? 'border-semantic-down/60' : 'border-hairline'}`}
      />
      {options && (
        <datalist id={id}>
          {options.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}
    </>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`t-caption-strong px-3 py-2 text-left text-muted ${className}`}>{children}</th>
}

function Count({
  label,
  value,
  tone = 'normal',
}: {
  label: string
  value: number | string
  tone?: 'normal' | 'down'
}) {
  // A count of zero is not a problem, so it never wears the problem colour.
  const flagged = tone === 'down' && value !== 0 && value !== ''
  return (
    <div>
      <p className="t-caption text-muted">{label}</p>
      <p className={`t-number-sm mt-0.5 ${flagged ? 'text-semantic-down' : 'text-ink'}`}>{value}</p>
    </div>
  )
}
